import { uniq } from 'es-toolkit';
import { Dispatch } from '@reduxjs/toolkit';

import { PathUtilities, QuantityMap } from '@paragon/layout-models';
import { enqueueError } from '@paragon/notification-tools';

export interface ProjectActionScope {
  readonly rowPaths: readonly string[];
  readonly componentDesignPaths: readonly string[];
}

export function getScopeComponentDesignGuids(scope: ProjectActionScope): string[] {
  return uniq(scope.componentDesignPaths.map(PathUtilities.getGuid));
}

export function getScopeProjectGuids(scope: ProjectActionScope): string[] {
  return uniq(scope.componentDesignPaths.map(PathUtilities.getProjectGuid));
}

function getSingleScopeProjectGuid(scope: ProjectActionScope): string | null {
  const projectGuids = getScopeProjectGuids(scope);
  return projectGuids.length === 1 ? projectGuids[0] : null;
}

/**
 * The single project guid the scope's components belong to; enqueues an error notification and returns null when the
 *   scope spans zero or several projects.
 */
export function requireSingleScopeProjectGuid(scope: ProjectActionScope, dispatch: Dispatch): string | null {
  const projectGuid = getSingleScopeProjectGuid(scope);
  if (projectGuid == null) {
    dispatch(enqueueError('Select components from one project'));
  }
  return projectGuid;
}

/**
 * How to derive a component design's quantity from the scope:
 * - `'fullQuantity'` multiplies the quantity up through every in-scope ancestor.
 * - `'oneOfParent'` multiplies only up to (one of) the topmost in-scope ancestor.
 */
export type ScopeQuantityBasis = 'fullQuantity' | 'oneOfParent';

export function getScopedComponentDesignQuantities(
  scope: ProjectActionScope,
  quantityMap: QuantityMap,
  basis: ScopeQuantityBasis,
): { [guid: string]: number } {
  const rowPathSet = new Set(scope.rowPaths);
  const componentDesignQuantities: { [guid: string]: number } = {};

  for (const componentDesignPath of scope.componentDesignPaths) {
    const componentDesignGuid = PathUtilities.getGuid(componentDesignPath);
    const componentDesignParentGuid = PathUtilities.getParentGuid(componentDesignPath);
    let quantity = quantityMap[componentDesignGuid][componentDesignParentGuid];

    for (const ancestorPath of PathUtilities.iterateUpAncestors(componentDesignPath)) {
      const relevantPathForDeterminingQuantity =
        basis === 'oneOfParent' ? PathUtilities.getParentPath(ancestorPath) : ancestorPath;

      if (!rowPathSet.has(relevantPathForDeterminingQuantity)) {
        break;
      }

      const ancestorPathGuid = PathUtilities.getGuid(ancestorPath);
      const ancestorPathParentGuid = PathUtilities.getParentGuid(ancestorPath);
      const quantityForCurrentPath = quantityMap[ancestorPathGuid][ancestorPathParentGuid];

      quantity *= quantityForCurrentPath;
    }

    componentDesignQuantities[componentDesignGuid] = quantity + (componentDesignQuantities[componentDesignGuid] ?? 0);
  }

  return Object.fromEntries(Object.entries(componentDesignQuantities).filter(([, quantity]) => quantity !== 0));
}
